import { useEffect, useState } from "react"
import styles from "@/components/CustomerSupport/CustomerSupport.view/index.module.scss"
import FormTabs from "@/components/CustomerSupport/FormTabs"
import ContactInfo from "@/components/CustomerSupport/Form/ContactInfo"
import { postCustomerSupport } from "@/utils/customerSupport"
import moment from "moment"
import {
  validateField,
  sanitizeObject,
  setBannerConfirmationText,
} from "@/utils/helper"
import Confirmation from "@/components/CustomerSupport/Confirmation"
import { validateEmail, validPhone } from "@/utils/validate"
import FeedbackForm from "@/components/CustomerSupport/Form/FeedbackForm"
import { selectAuthState } from "@/store/features/authSlice"
import { useSelector } from "react-redux"
import {
  backToCustomerSupportAnalytics,
  cancelAnalytics,
  serviceRequestOrFeedbackAnalytics,
  submitAnalytics,
} from "../analytics"

const CustomerSupportView = props => {
  const {
    activeTab,
    setActiveTab,
    staticText,
    tabTypes,
    serviceRequest,
    productDetails,
    authorData,
  } = props
  const { user, isAuth } = useSelector(selectAuthState)
  const initFormData = {
    fname: "",
    lname: "",
    email: "",
    address: "",
    landmark: "",
    pincode: "",
    state: "",
    city: "",
    phone: { value: "", error: false, message: "" },
    countryCode: "+91",
    atlernateNumber: "",
    dateInvoice: "",
    serviceRequest: "",
    productDetails: "",
    reportIssue: "",
  }
  const [userData, setUserData] = useState(initFormData)

  const [error, setError] = useState({})
  const [loading, setLoading] = useState(false)
  const [verified, setVerified] = useState(false)
  const [success, setSuccess] = useState(false)
  const [pincodeValid, setPincodeValid] = useState(false)
  const [initialBannerText, setInitialBannerText] = useState("")

  useEffect(() => {
    const validSession =
      sessionStorage?.getItem("okt.user.temp.details") &&
      sessionStorage?.getItem("okt.user.temp.verify") &&
      atob(sessionStorage?.getItem("okt.user.temp.verify"))?.length > 0 &&
      atob(sessionStorage?.getItem("okt.user.temp.details"))?.length > 0
        ? true
        : false
    setVerified(validSession)
  }, [])

  useEffect(() => {
    const banner = document?.getElementById("support-form-banner")
    const bannerDesc = banner?.getElementsByClassName(
      "cmp-teaser__description"
    )?.[0]
    const bannerDescElem = bannerDesc?.getElementsByTagName("p")?.[0]
    if (bannerDescElem) {
      setInitialBannerText(bannerDescElem.innerText)
    }
  }, [])

  useEffect(() => {
    setSuccess(false)
    setVerified(false)
    setError({})
    setUserData(initFormData)
    if (initialBannerText) {
      setBannerConfirmationText(initialBannerText, "support-form-banner")
    }
  }, [activeTab])

  const validateExist = field => {
    switch (field) {
      case "fname":
      case "lname":
        if (
          !userData[field]?.trim() ||
          !validateField("name", userData[field])
        ) {
          return field === "fname"
            ? staticText?.fnameError
            : staticText?.lnameError
        }
        break
      case "email":
        if (!userData[field]) return staticText?.noEmailError
        else if (
          !validateField("email", userData[field]) ||
          !validateEmail(userData[field])
        )
          return staticText?.validEmail
        break
      case "atlernateNumber":
        if (!validPhone(userData[field]) && userData[field]?.length > 0)
          return staticText?.validPhone
        break
      case "address":
        if (!userData?.address?.trim()) return staticText?.addressError
        break
      case "city":
        if (!userData?.city?.trim()) return staticText?.cityError
        break
      case "state":
        if (!userData?.state?.trim()) return staticText?.stateError
        break
      case "pincode":
        if (!userData?.pincode?.trim()) return staticText?.errorPincode
        else if (userData?.pincode?.length !== 6)
          return staticText?.pincodeValid
        else if (pincodeValid) return staticText?.pincodeNotValid
        break
      case "dateInvoice":
        if (!userData?.dateInvoice?.trim()) return staticText?.cityError
        break
      case "serviceRequest":
        if (!userData?.serviceRequest?.trim())
          return staticText?.errorServiceRequest
        break
      case "productDetails":
        if (!userData?.productDetails?.trim())
          return staticText?.errorProductDetails
        break
      case "reportIssue":
        if (!userData?.reportIssue?.trim()) return staticText?.errorReportIssue
        break
      default:
        return
    }
  }

  const onBlurError = field => {
    setError({
      ...error,
      [field]: validateExist(field),
    })
  }

  const getErrorList = () => {
    let errorList = {
      fname: validateExist("fname"),
      lname: validateExist("lname"),
      email: validateExist("email"),
      address: validateExist("address"),
      city: validateExist("city"),
      state: validateExist("state"),
      pincode: validateExist("pincode"),
      reportIssue: validateExist("reportIssue"),
      serviceRequest: validateExist("serviceRequest"),
      productDetails: validateExist("productDetails"),
    }

    return errorList
  }

  const isEnabled = Object?.values(getErrorList())?.find(el => el)
    ? true
    : false

  const isDisabledCTA = isEnabled ? true : verified ? false : true

  const onSubmit = () => {
    setLoading(true)
    let errorDataList = []
    const errorList = getErrorList()
    setError(errorList)

    if (errorList?.fname) {
      errorDataList.push(`contact-user-fname`)
    } else if (errorList?.lname) {
      errorDataList.push(`contact-user-lname`)
    } else if (errorList?.email) {
      errorDataList.push(`contact-user-email`)
    } else if (errorList?.address) {
      errorDataList.push(`contact-user-address`)
    } else if (errorList?.city) {
      errorDataList.push(`contact-user-city`)
    } else if (errorList?.state) {
      errorDataList.push(`contact-user-state`)
    } else if (errorList?.pincode) {
      errorDataList.push(`contact-user-pincode`)
    } else if (errorList?.serviceRequest) {
      errorDataList.push(`contact-installed-serviceRequest`)
    } else if (errorList?.reportIssue) {
      errorDataList.push(`contact-user-productDetails`)
    } else if (errorList?.reportIssue) {
      errorDataList.push(`contact-user-reportIssue`)
    }

    errorDataList.map((item, i) => {
      if (i === 0) document.getElementById(`${item}`)?.focus()
    })

    const isInvalid = Object?.values(errorList)?.find(el => el)
    if (!isInvalid) {
      const {
        fname,
        lname,
        email,
        address,
        landmark,
        city,
        state,
        pincode,
        phone,
        countryCode,
        atlernateNumber,
        dateInvoice,
        serviceRequest,
        productDetails,
        reportIssue,
      } = userData

      let body = {
        firstName: fname?.trim(),
        lastName: lname?.trim(),
        email: email,
        addressLine1: address,
        landmark: landmark,
        countryCode: "+91",
        state: state,
        city: city,
        pincode: pincode,
        mobile: phone?.value,
        atlernateNumber: atlernateNumber,
        dateOfInvoice: dateInvoice
          ? moment(dateInvoice)?.format("DD/MM/YYYY")
          : "",
        serviceRequest: serviceRequest,
        productDetail: productDetails,
        reportIssue: reportIssue,
      }

      if (isAuth) {
        body["userId"] = user?.uid ?? null
      }

      sanitizeObject(body)

      postCustomerSupport(body).then(resp => {
        const { statusCode, result = {} } = resp

        if (statusCode === 200) {
          submitAnalytics({ status: "success", serviceRequest, productDetails })
          const res = result.data
          setBannerConfirmationText(
            staticText.bannerConfirmationText,
            "support-form-banner"
          )
          setSuccess(true)
          scrollToTop()
          setLoading(false)
        }
        return { statusCode }
      })
    } else {
      submitAnalytics({ status: "failure", statusMsg: "form submition failed" })
      setLoading(false)
      let allErrors = []
      for (const key in errorList) {
        if (errorList[key]) {
          allErrors.push(errorList[key].toLowerCase())
        }
      }
    }
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  }

  const resetData = (isConfirmScreen) => {
    const firstName = user?.firstName ?? ""
    const lastName = user?.lastName ?? ""
    const email = user?.sub ?? ""
    setUserData({
      ...userData,
      fname: firstName,
      lname: lastName,
      email: email,
      address: "",
      landmark: "",
      pincode: "",
      state: "",
      city: "",
      countryCode: "+91",
      atlernateNumber: "",
      dateInvoice: "",
      serviceRequest: "",
      productDetails: "",
      reportIssue: "",
    })
    isConfirmScreen ? backToCustomerSupportAnalytics('service request') : cancelAnalytics()
    setError({})
    setSuccess(false)
    scrollToTop()
  }

  return (
    <>
      <div className={styles.customerSupportLanding}>
        <div className="customer-support">
          <div className="customer-support__title">
            <h2 className="customer-support__question">
              {staticText?.mainTitle}
            </h2>
            <span className="customer-support__required-fields">
              {staticText?.requiredInfoText}
            </span>
          </div>
          <FormTabs
            label={tabTypes}
            activeTab={activeTab}
            changeActive={tab => {
              setActiveTab(tab)
              serviceRequestOrFeedbackAnalytics(tab)
              setError({})
            }}
          />

          {activeTab === "Feedback" && (
            <FeedbackForm
              staticText={staticText}
              authorData={authorData}
              resetTab={() => setActiveTab("Service Request")}
            />
          )}
          {activeTab === "Service Request" &&
            (success ? (
              <Confirmation staticText={staticText} resetData={() => resetData(true)} />
            ) : (
              <ContactInfo
                key={activeTab}
                serviceRequest={serviceRequest}
                productDetails={productDetails}
                activeTab={activeTab}
                staticText={staticText}
                data={userData}
                setData={setUserData}
                verified={verified}
                setVerified={setVerified}
                error={error}
                setError={setError}
                onBlur={onBlurError}
                onSubmit={onSubmit}
                resetData={() => resetData(false)}
                isEnabled={isDisabledCTA}
                setPincodeValid={setPincodeValid}
              />
            ))}
        </div>
      </div>
    </>
  )
}

export default CustomerSupportView
